import { useEffect } from 'react';

/**
 * useBodyScrollLock — modal/popover open 중 body scroll 차단 표준 hook.
 *
 * modal hook quartet 의 4번째 (useModalLeave + useFocusTrap + useEscapeClose +
 * useBodyScrollLock). active=true 시 body 의 overflow:hidden 적용 → 사용자가
 * modal 외부 스크롤 못 함. cleanup 시 이전 overflow 값 복원.
 *
 * 사용:
 *   const { shouldRender } = useModalLeave(open, 200);
 *   useBodyScrollLock(shouldRender);
 *
 * leaving phase 와의 결합:
 *   active=shouldRender 직접 전달 — leaving 200ms 동안에도 scroll lock 유지.
 *   사용자가 leaving 중 외부 스크롤 시도해도 차단.
 *
 * 다중 modal scope:
 *   2개 이상 modal 이 동시 open 시 (예: nested) 양쪽 hook 호출 — 각자 cleanup
 *   에서 *직전 값* 복원 → stack 문서화. 본 hook 은 *직전 값 복원* 정책 (counter
 *   기반 stack 추적은 별도 chain). 본 프로젝트 nested modal 0건이라 단순 정책
 *   충분.
 *
 * scrollbar gutter 보정:
 *   active=true 시 scrollbar 가 사라져 viewport width 가 변경 → content 가
 *   우측으로 jump (layout shift). active=true 동안 body padding-right 에 사라진
 *   scrollbar 폭 만큼 채워 layout shift 봉합. cleanup 시 이전 padding-right 값
 *   복원.
 *
 * iOS position:fixed 트릭 (cross-browser 일관 적용):
 *   adr-20260507-ios-position-fixed-body-scroll-lock §2. iOS Safari 의
 *   momentum scrolling + scroll position 리셋 봉합. body 자체를 fixed 로 변경
 *   + 현재 scrollY 를 top:-${scrollY}px 로 캐싱 → 시각 위치 보존 + 외부 스와이프
 *   차단. cleanup 시 모든 style 복원 + window.scrollTo(0, scrollY) 동기 호출.
 *   UA sniffing 회피 — 모든 브라우저 동일 적용으로 *예측 가능한 single 동작*.
 *
 * 비특징 (deferred):
 *   - CSS scrollbar-gutter:stable 전역 적용 — 본 hook 의 동적 padding-right 와
 *     상호 충돌 가능. 본 hook 은 *동적 보정* 정책.
 *   - nested modal scroll lock counter — 본 프로젝트 0건 (ADR R2). 도입 시 별도
 *     cycle.
 */

export function useBodyScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    // 모든 mutate 대상 style 의 *직전 값* 캐싱 (cleanup 복원).
    const prev = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
      paddingRight: document.body.style.paddingRight,
    };
    // scrollY + scrollbar 폭 — overflow:hidden 적용 *전* 측정해야 정확.
    const scrollY = window.scrollY;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    // position:fixed 트릭 — body 자체가 fixed 가 되면 width 가 0 으로 collapse
    // 가능. width:100% + left:0 + right:0 명시. top 에 -scrollY 로 *시각 위치
    // 보존* — 사용자가 modal close 후 같은 자리에 있는 것처럼 보임.
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      // 모든 style 복원 — Object.assign 으로 일괄.
      Object.assign(document.body.style, prev);
      // scroll position 복원 — fixed 해제 직후 동기 호출 (1 frame 이내).
      window.scrollTo(0, scrollY);
    };
  }, [active]);
}
